import { DatePipe, NgClass, KeyValuePipe } from '@angular/common';
import { Component, HostListener, Input, OnInit, ViewChild, inject } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ExceptionWorkingDayModel } from 'src/app/models/exceptionWorkingDay.model';
import { ScheduleDatesFilterModel } from 'src/app/models/filter-date.model';
import { ScheduleExceptionModel } from 'src/app/models/scheduleException.model';
import { SupplierWorkingDaysSetModel } from 'src/app/models/supplier-workingDay-set.model';
import { SupplierWorkingDaysModel } from 'src/app/models/supplier-workingDay.model';
import { SupplierModel } from 'src/app/models/supplier.model';
import { WorkingDaySetModel } from 'src/app/models/workingDay-set.model';
import { WorkingHoursModel } from 'src/app/models/workingHours.model';
import { SupplierService } from 'src/app/services/supplier.services';
import { ToastService } from 'src/app/services/toast.service';
import { months } from 'src/assets/data/months.config';
import { weekDays } from 'src/assets/data/week-days.config';
import { MatTabGroup, MatTab } from '@angular/material/tabs';
import { MatIcon } from '@angular/material/icon';
import { MatCheckbox } from '@angular/material/checkbox';
import { RangeDatesComponent } from '../../../shared/components/range-dates/range-dates.component';
import { CommonFunctionsConfiguratorService } from 'src/app/shared/services/swal-fire-configurator.service';
import { escapeBelongsToOverlay } from '../escape-key';

@Component({
    selector: 'app-supplier-workingDays',
    templateUrl: './supplier-workingDays.component.html',
    styleUrls: ['./supplier-workingDays.component.scss'],
    imports: [MatTabGroup, MatTab, NgClass, MatIcon, MatCheckbox, FormsModule, ReactiveFormsModule, RangeDatesComponent, KeyValuePipe]
})
export class SupplierWorkingDaysComponent implements OnInit {
  private readonly supplierService = inject(SupplierService);
  datepipe = inject(DatePipe);
  activatedRoute = inject(ActivatedRoute);
  private readonly router = inject(Router);
  toastr = inject(ToastService);
  private readonly swalFire = inject(CommonFunctionsConfiguratorService);


  @Input() supplier!: SupplierModel;
  fromHour: string = '00:00';
  toHour: string = '00:00';
  minToHour: string = '00:00';
  errorMessage: string = '';
  selectedDays: number[] = [];
  workingDaysSummary: string[] = [];
  supplierWorkingDays: SupplierWorkingDaysModel[] = [];
  exceptionWorkingDays: ExceptionWorkingDayModel[] = [];
  // The exception modal renders app-range-dates (it used to query DatesFilterComponent, which is not in the template,
  // so resetting it threw "Cannot read properties of undefined (reading 'resetForm')").
  @ViewChild(RangeDatesComponent) filterComponent?: RangeDatesComponent;

  minFromDate = new Date();
  minToDate = new Date();
  scheduleExceptionForm = new UntypedFormGroup({});
  exceptionGroupsByMonth!: any[];

  weekDays = weekDays;

  supplierId!: string;
  edit: boolean = false;
  showExceptionDaysCalendar: boolean = false;

  ngOnInit(): void {
    this.supplierId = this.supplier.id.toString();
    let dateTo = new Date();
    dateTo.setDate(dateTo.getDate() + 1);

    this.scheduleExceptionForm = new UntypedFormGroup({
      from: new UntypedFormControl(null, Validators.required),
      to: new UntypedFormControl(null, Validators.required),
      contractorId: new UntypedFormControl(''),
    });
    this.minToDate = dateTo;

    if (this.supplierId) {
      this.scheduleExceptionForm.controls['contractorId'].patchValue(
        this.supplierId
      );
      this.getWorkingDays(this.supplierId, true);
    }
  }

  onSubmit() {
    let model: SupplierWorkingDaysSetModel = {
      contractorId: this.supplierId.toString(),
      workingDays: [],
    };

    // Validate every checked day first: one wrong day stops the save with its own message only.
    const errors: string[] = [];
    this.selectedDays.forEach((item) => {
      const wd = this.supplierWorkingDays.find((w) => w.day === item);
      if (!wd) {
        return;
      }
      if (!wd.fromHour || !wd.toHour) {
        errors.push(`Debe ingresar el horario del día ${this.getDayDescription(item)}.`);
        return;
      }

      // Convierte las horas de cada día usando el formato de 'wd.fromHour' y 'wd.toHour'
      let from = new Date(
        Date.UTC(
          1900,
          1,
          1,
          Number.parseInt(wd.fromHour.split(':')[0]),
          Number.parseInt(wd.fromHour.split(':')[1]) + new Date().getTimezoneOffset()
        )
      );

      let to = new Date(
        Date.UTC(
          1900,
          1,
          1,
          Number.parseInt(wd.toHour.split(':')[0]),
          Number.parseInt(wd.toHour.split(':')[1]) + new Date().getTimezoneOffset()
        )
      );

      if (to <= from) {
        errors.push(
          `La franja horaria seleccionada para el día ${this.getDayDescription(item)} no es correcta.`
        );
        return;
      }

      const workingDay: WorkingDaySetModel = {
        day: item,
        fromHour: from,
        toHour: to,
      };
      model.workingDays.push(workingDay);
    });

    if (errors.length > 0) {
      errors.forEach((message) => this.toastr.error('Error', message));
      return;
    }

    if (model.workingDays.length === 0) {
      return this.toastr.error(
        'Error',
        'Debe seleccionar al menos un día con horario.'
      );
    }

    this.supplierService.contractorSchedules(model).subscribe({
      next: (data) => {
        this.toastr.success(
          'Actualización',
          'Se han actualizado los horarios del prestador correctamente.'
        );
        this.getWorkingDays(this.supplierId);
        this.reset();
        this.edit = false;
      },
      error: (err) => {
        this.toastr.error('Error', 'Hay horario(s) superpuesto(s).');
      },
    });
  }

  editHour() {
    this.edit = true;
  }

  openModal() {
    this.showExceptionDaysCalendar = true;
  }

  @HostListener('window:keydown.escape', ['$event'])
  onEscape(event: KeyboardEvent) {
    // Only while the exception modal is open, and not when Escape just closed an open overlay inside it. Its range
    // calendar is always open (it reopens itself), so it doesn't count as an overlay to close first.
    if (this.showExceptionDaysCalendar && !escapeBelongsToOverlay(event, { ignoreCalendars: true })) {
      this.closeModal();
    }
  }

  closeModal() {
    this.showExceptionDaysCalendar = false;
    this.resetException();
  }

  saveExceptionDays() {
    if (this.scheduleExceptionForm.invalid) {
      this.toastr.error('Error', 'Debe seleccionar al menos una fecha.');
      return;
    }

    if (this.scheduleExceptionForm.valid) {
      let model = this.scheduleExceptionForm.value as ScheduleExceptionModel;

      this.supplierService.exceptionsPost(model).subscribe({
        next: (data) => {
          this.getExceptions(model.contractorId);
          this.showExceptionDaysCalendar = false;
          this.resetException();
        },
        error: (err) => {
          this.toastr.error(
            'Error',
            'No se han podido guardar la nueva excepción.'
          );
        },
      });
    }
  }

  setException(dates: ScheduleDatesFilterModel) {
    this.scheduleExceptionForm.controls.from.patchValue(dates.from);
    this.scheduleExceptionForm.controls.to.patchValue(dates.to);
  }

  getExceptions(contractorId: string) {
    this.supplierService.exceptionsGet(contractorId).subscribe((data) => {
      this.exceptionWorkingDays = data;
      this.groupExceptions();
    });
  }

  changeFrom(event: any) {
    this.minToDate = new Date(event.value);
    this.minToDate.setDate(this.minToDate.getDate() + 1);
  }

  onCheckChange(event: any) {
    if (event.checked) {
      this.selectedDays.push(Number(event.source.value));
    } else {
      this.selectedDays.forEach((item, index) => {
        if (item == event.source.value) this.selectedDays.splice(index, 1);
      });
    }
  }

  validateCheck(id: any): boolean {
    return this.selectedDays.some((x) => x == id);
  }

  getWorkingDays(id: any, getExceptions: boolean = false) {
    this.supplierService.getSchedules(id).subscribe((model) => {
      const sortedWorkingDays = this.sortingWorkingDays(model);

      this.supplierWorkingDays = weekDays.map((day) => {
        //Busca si el día actual tiene horario en los datos que devuelve el endpoint
        const workingDay = sortedWorkingDays.find(
          (wd: { day: number }) => wd.day === day.id
        );

        // Si el día tiene horarios, crea el arreglo `workingHours`, si no, será un arreglo vacío
        const workingHours: WorkingHoursModel[] = workingDay?.workingHours
          ?.length
          ? workingDay.workingHours.map((wh: WorkingHoursModel) => ({
              id: wh.id,
              startHour: wh.startHour,
              endHour: wh.endHour,
            }))
          : [];

        // Si no hay horarios de trabajo, asignamos "Sin horario"
        const hoursDisplay = workingHours.length
          ? workingHours
              .map(
                (wh) =>
                  `${this.formatDate(wh.startHour)} - ${this.formatDate(
                    wh.endHour
                  )}`
              )
              .join(',  ')
          : 'Sin horario';
        return {
          id: workingDay?.id || null,
          day: day.id,
          fromHour: '',
          toHour: '',
          workingHours: workingHours,
          hoursDisplay: hoursDisplay,
          errorMessage: '',
        };
      });

      if (getExceptions) {
        this.getExceptions(id);
      }
    });
  }

  sortingWorkingDays(model: any) {
    return model.sort(function (a: any, b: any) {
      return a.day - b.day;
    });
  }

  formatDate(date: any) {
    // Verifica si el valor es válido antes de intentar formatearlo
    if (!date) return 'Sin horario';

    let ant = new Date(Date.parse(date));
    let localDate = new Date(
      ant.getUTCFullYear(),
      ant.getUTCMonth(),
      ant.getUTCDay(),
      ant.getUTCHours(),
      ant.getUTCMinutes() - new Date().getTimezoneOffset()
    );
    return this.datepipe.transform(localDate, 'HH:mm');
  }

  getDayDescription(id: number) {
    return weekDays.find((x) => x.id == id)?.longName;
  }

  isDeleting = false;
  deleteHour(dayId: any, hourId: any) {
    if (this.isDeleting) {
      return;
    }

    this.isDeleting = true;

    this.supplierService.deleteHourSchedule(dayId, hourId).subscribe(
      (data) => {
        this.getWorkingDays(this.supplierId);
        this.edit = false;
        this.isDeleting = false;
      },
      (err) => {
        this.toastr.error('Error', 'No se ha podido eliminar el horario');
        this.isDeleting = false;
      }
    );
  }

  deleteDay(dayId: any) {
    this.supplierService.deleteDaySchedule(dayId).subscribe(
      (data) => {
        this.getWorkingDays(this.supplierId);
      },
      (err) => {
        this.toastr.error('Error', 'No se ha podido eliminar el dia');
      }
    );
  }

  async deleteException(id: any) {
    const result = await this.swalFire.SwalFireQuestion(
      'Eliminación',
      '¿Desea eliminar esta excepción?',
      'Eliminar',
      'Cancelar'
    );
    if (!result.isConfirmed) {
      return;
    }

    this.supplierService.exceptionsDelete(id).subscribe(
      (data) => {
        this.exceptionWorkingDays = this.exceptionWorkingDays.filter(
          (e) => e.id !== id
        );
        this.groupExceptions();
      },
      (err) => {
        this.toastr.error('Error', 'No se ha podido eliminar la excepción.');
      }
    );
  }

  timeChanged(newHours: string, dayIndex: number, isFromHour: boolean) {
    // The typed range lives in fromHour/toHour (ngModel) and is what onSubmit saves. It used to also overwrite the
    // day's first saved hour, which does not exist on a day without hours (TypeError reading 'startHour').
    this.validateToHour(dayIndex);
  }

  validateToHour(dayIndex: number) {
    const workingDay = this.supplierWorkingDays[dayIndex];
    if (workingDay.toHour && workingDay.fromHour) {
      if (workingDay.toHour <= workingDay.fromHour) {
        workingDay.errorMessage =
          'La hora de fin no puede ser menor que la hora de inicio.';
      } else {
        workingDay.errorMessage = '';
      }
    }
  }

  convertToDate(timeString: string): Date {
    const [hours, minutes] = timeString.split(':').map(Number);
    const date = new Date(2000, 0, 1, hours, minutes); // Se usa una fecha arbitraria para trabajar solo con hora
    return date;
  }

  backToList() {
    this.router.navigate(['/supplierList']);
  }

  reset() {
    this.fromHour = this.toHour = this.minToHour = '00:00';
    this.selectedDays = [];
  }

  resetException() {
    this.scheduleExceptionForm.reset();
    this.scheduleExceptionForm.controls.contractorId.patchValue(
      this.supplier.id
    );
    this.filterComponent?.resetForm();
  }

  getDate(date: Date) {
    return this.datepipe.transform(date, 'dd/MM/yyyy');
  }

  groupExceptions() {
    let prepare: object[] = [];

    for (const element of this.exceptionWorkingDays) {
      let dateSplit = element.day.toString().split('-');
      prepare.push({
        key: dateSplit[0] + '|' + dateSplit[1],
        value: element,
      });
    }

    const groupBy = (array: any[], key: string) => {
      return array.reduce((result, currentValue) => {
        const groupKey = currentValue[key];

        // Inicializa el grupo si no existe
        if (!result[groupKey]) {
          result[groupKey] = [];
        }

        // Agrega el valor actual al grupo correspondiente
        result[groupKey].push(currentValue);

        return result;
      }, {});
    };

    this.exceptionGroupsByMonth = groupBy(prepare, 'key');
  }

  getTabDesc(key: string) {
    let values = key.split('|');
    let month = months.find((x) => x.id == (values[1] as unknown as number));
    return month?.longName + ' de ' + values[0];
  }

  getOffSet(): string {
    let offSet = new Date().getTimezoneOffset() / -60;
    let offSetFormated = Math.abs(offSet).toString().padStart(2, '0');
    return (offSet >= 0 ? '+' : '-') + offSetFormated + ':00';
  }

  onElementKeydown(event: KeyboardEvent, element: any) {
    if (event.key === 'Enter') {
      this.deleteException(element);
    }
  }
}
